/** In-memory store. Tests, and a last-resort fallback when IndexedDB is unavailable. */
export class MemoryOutboxStore {
    #events = new Map();
    async put(events) {
        for (const e of events)
            this.#events.set(e.eventId, e);
    }
    async peek(limit) {
        // Insertion order is arrival order; sort by (session, seq) for deterministic
        // in-order delivery even when batches interleave across sessions.
        return [...this.#events.values()]
            .sort((a, b) => a.sessionId === b.sessionId
            ? a.seq - b.seq
            : a.sessionId.localeCompare(b.sessionId))
            .slice(0, limit);
    }
    async remove(eventIds) {
        for (const id of eventIds)
            this.#events.delete(id);
    }
    async count() {
        return this.#events.size;
    }
    async clear() {
        this.#events.clear();
    }
}
/**
 * IndexedDB-backed store. Keyed by eventId so a replayed write is a no-op, with a
 * (sessionId, seq) index for ordered draining.
 */
export class IdbOutboxStore {
    #dbPromise = null;
    #dbName;
    #storeName = "outbox";
    /**
     * One database per learner, not one per browser.
     *
     * **A shared device is the normal case here**, and a single outbox on it is a real fault rather than
     * an inefficiency. A child plays, some events are still queued, the tablet is handed back and a
     * teacher signs in — and the queue then flushes under the teacher's session, which the server
     * correctly refuses (R-3). Nothing drains it afterwards, so every later flush fails too and the
     * child's remaining trials are stranded behind a 403 that repeats forever. It was reported exactly
     * that way: sign out of the teacher, sign in as the student, and the console fills with 403s.
     *
     * Naming the database after the learner makes the queue unreachable from any other account: a
     * session that cannot open it cannot send it, and the events wait — safely — for the child to come
     * back rather than being offered to whoever is signed in now.
     */
    constructor(dbName = "talent-telemetry") {
        this.#dbName = dbName;
    }
    /** The database name for one learner. Namespaced, so two children on one device never mix. */
    static forProfile(profileId) {
        return new IdbOutboxStore(`talent-telemetry:${profileId}`);
    }
    #db() {
        this.#dbPromise ??= new Promise((resolve, reject) => {
            const req = indexedDB.open(this.#dbName, 1);
            req.onupgradeneeded = () => {
                const db = req.result;
                if (!db.objectStoreNames.contains(this.#storeName)) {
                    const os = db.createObjectStore(this.#storeName, { keyPath: "eventId" });
                    os.createIndex("bySessionSeq", ["sessionId", "seq"]);
                }
            };
            req.onsuccess = () => resolve(req.result);
            req.onerror = () => reject(req.error);
        });
        return this.#dbPromise;
    }
    async #tx(mode, fn) {
        const db = await this.#db();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(this.#storeName, mode);
            const req = fn(tx.objectStore(this.#storeName));
            tx.oncomplete = () => resolve(req ? req.result : undefined);
            tx.onerror = () => reject(tx.error);
            tx.onabort = () => reject(tx.error);
        });
    }
    async put(events) {
        const db = await this.#db();
        await new Promise((resolve, reject) => {
            const tx = db.transaction(this.#storeName, "readwrite");
            const os = tx.objectStore(this.#storeName);
            for (const e of events)
                os.put(e);
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error);
            tx.onabort = () => reject(tx.error);
        });
    }
    async peek(limit) {
        const db = await this.#db();
        return new Promise((resolve, reject) => {
            const out = [];
            const tx = db.transaction(this.#storeName, "readonly");
            const idx = tx.objectStore(this.#storeName).index("bySessionSeq");
            const cursorReq = idx.openCursor();
            cursorReq.onsuccess = () => {
                const cursor = cursorReq.result;
                if (!cursor || out.length >= limit)
                    return;
                out.push(cursor.value);
                cursor.continue();
            };
            tx.oncomplete = () => resolve(out);
            tx.onerror = () => reject(tx.error);
        });
    }
    async remove(eventIds) {
        const db = await this.#db();
        await new Promise((resolve, reject) => {
            const tx = db.transaction(this.#storeName, "readwrite");
            const os = tx.objectStore(this.#storeName);
            for (const id of eventIds)
                os.delete(id);
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error);
        });
    }
    async count() {
        return (await this.#tx("readonly", (os) => os.count())) ?? 0;
    }
    async clear() {
        await this.#tx("readwrite", (os) => {
            os.clear();
            return null;
        });
    }
}
//# sourceMappingURL=store.js.map